import urls from "../global/constants/UrlConstants";
import { getCallParams, makeCall } from "../utils/service";

const base = urls.url_prod;

/** Webhook URL + payloads — works without Meta account connected */
export async function getMetaWebhookInfo() {
  const callParams = await getCallParams("GET");
  return makeCall(`${base}/meta/webhook-info`, callParams);
}

/** Form-specific webhook URL + dynamic payload from form fields */
export async function getFormWebhookInfo(formId: string) {
  const callParams = await getCallParams("GET");
  return makeCall(`${base}/meta/forms/${formId}/webhook-info`, callParams);
}

export async function getMetaStatus(organizationId: string) {
  const callParams = await getCallParams("GET");
  return makeCall(
    `${base}/meta/organizations/${organizationId}/status`,
    callParams
  );
}

export async function listMetaPages(organizationId: string) {
  const callParams = await getCallParams("GET");
  return makeCall(
    `${base}/meta/organizations/${organizationId}/pages`,
    callParams
  );
}

export async function connectMetaPage(
  organizationId: string,
  pageId: string
) {
  const callParams = await getCallParams("POST", { pageId });
  return makeCall(
    `${base}/meta/organizations/${organizationId}/pages/connect`,
    callParams
  );
}

export async function syncMetaForms(
  organizationId: string,
  pageId?: string
) {
  const callParams = await getCallParams("POST", pageId ? { pageId } : {});
  return makeCall(
    `${base}/meta/organizations/${organizationId}/forms/sync`,
    callParams
  );
}

export async function listMetaForms(
  organizationId: string,
  campaignId?: string,
  enrich = false
) {
  const qs = new URLSearchParams();
  if (campaignId) qs.set("campaignId", campaignId);
  if (enrich) qs.set("enrich", "1");
  const query = qs.toString();
  const callParams = await getCallParams("GET");
  return makeCall(
    `${base}/meta/organizations/${organizationId}/forms${
      query ? `?${query}` : ""
    }`,
    callParams
  );
}

export async function listMetaCampaigns(
  organizationId: string,
  refresh = false,
  pageId?: string
) {
  const qs = new URLSearchParams();
  if (refresh) qs.set("refresh", "1");
  if (pageId) qs.set("pageId", pageId);
  const query = qs.toString();
  const callParams = await getCallParams("GET");
  return makeCall(
    `${base}/meta/organizations/${organizationId}/campaigns${
      query ? `?${query}` : ""
    }`,
    callParams
  );
}

export async function syncMetaCampaigns(
  organizationId: string,
  pageId?: string
) {
  const callParams = await getCallParams("POST", pageId ? { pageId } : {});
  return makeCall(
    `${base}/meta/organizations/${organizationId}/campaigns/sync`,
    callParams
  );
}

export async function connectMetaCampaign(
  organizationId: string,
  campaignId: string
) {
  const callParams = await getCallParams("POST", { campaignId });
  return makeCall(
    `${base}/meta/organizations/${organizationId}/campaigns/connect`,
    callParams
  );
}

export async function connectAllMetaPages(organizationId: string) {
  const callParams = await getCallParams("POST", {});
  return makeCall(
    `${base}/meta/organizations/${organizationId}/pages/connect-all`,
    callParams
  );
}

export async function mapMetaForm(
  organizationId: string,
  body: { metaFormId: string; crmFormId: string }
) {
  const callParams = await getCallParams("PUT", body);
  return makeCall(
    `${base}/meta/organizations/${organizationId}/forms/map`,
    callParams
  );
}

export async function disconnectMeta(organizationId: string) {
  const callParams = await getCallParams("POST", {});
  return makeCall(
    `${base}/meta/organizations/${organizationId}/disconnect`,
    callParams
  );
}

/**
 * Open Meta OAuth in a centered popup. Resolves when popup posts
 * YHATAW_META_OAUTH or closes.
 */
export function openMetaOAuthPopup(organizationId: string): Promise<{
  success: boolean;
  message?: string;
}> {
  const url = `${base}/meta/oauth/start?organizationId=${encodeURIComponent(
    organizationId
  )}`;
  const w = 680;
  const h = 760;
  const left = Math.max(0, window.screenX + (window.outerWidth - w) / 2);
  const top = Math.max(0, window.screenY + (window.outerHeight - h) / 2);
  const popup = window.open(
    url,
    "yhataw_meta_oauth",
    `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`
  );

  return new Promise((resolve) => {
    if (!popup) {
      resolve({
        success: false,
        message: "Popup blocked — allow popups for this site and try again",
      });
      return;
    }

    let settled = false;
    const finish = (result: { success: boolean; message?: string }) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("message", onMessage);
      clearInterval(timer);
      try {
        if (!popup.closed) popup.close();
      } catch (_) {}
      resolve(result);
    };

    const onMessage = (event: MessageEvent) => {
      const data = event.data;
      if (!data || data.type !== "YHATAW_META_OAUTH") return;
      finish({
        success: !!data.success,
        message: data.message,
      });
    };
    window.addEventListener("message", onMessage);

    const timer = window.setInterval(() => {
      if (popup.closed) {
        finish({
          success: false,
          message: "Meta login window was closed before finishing",
        });
      }
    }, 600);
  });
}
